import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { X, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AlertsPanel = ({ fieldId }) => {
  const { getAuthHeaders } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAlerts();
  }, [fieldId]);

  const loadAlerts = async () => {
    try {
      const response = await axios.get(`${API}/fields/${fieldId}/alerts`, { headers: getAuthHeaders() });
      setAlerts(response.data || []);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'border-red-500 bg-red-50';
      case 'medium':
        return 'border-orange-500 bg-orange-50';
      case 'info':
        return 'border-blue-500 bg-blue-50';
      default:
        return 'border-gray-500 bg-gray-50';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'medium':
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      default:
        return <Info className="w-5 h-5 text-blue-600" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      disease: 'Disease',
      pest: 'Pest',
      weather: 'Weather',
      fertility: 'Fertilizer',
      setup: 'Setup',
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="animate-pulse">Loading alerts...</div>;
  }

  if (alerts.length === 0) {
    return (
      <div className="card p-6 text-center">
        <Info className="w-12 h-12 mx-auto mb-3 text-[rgb(var(--muted-foreground))]" />
        <p className="text-[rgb(var(--muted-foreground))]">No alerts at this time. Monitoring continues.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="alerts-panel">
      {alerts.map((alert, index) => (
        <div
          key={alert.id || index}
          className={`card p-5 border-l-4 ${getSeverityColor(alert.severity)}`}
          data-testid={`alert-${alert.id}`}
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5">{getSeverityIcon(alert.severity)}</div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 text-xs rounded-full bg-[rgb(var(--muted))] font-medium">
                  {getTypeLabel(alert.type)}
                </span>
                <h3 className="font-semibold text-lg">{alert.title}</h3>
              </div>
              
              <p className="text-[rgb(var(--muted-foreground))] mb-3">{alert.summary}</p>
              
              {alert.why && alert.why.length > 0 && (
                <div className="mb-3">
                  <p className="font-medium text-sm mb-1">Why:</p>
                  <ul className="text-sm space-y-1 text-[rgb(var(--muted-foreground))]">
                    {alert.why.map((reason, i) => (
                      <li key={i}>• {reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {alert.actions && alert.actions.length > 0 && (
                <div>
                  <p className="font-medium text-sm mb-1">Actions:</p>
                  <ul className="text-sm space-y-1 text-[rgb(var(--primary))]">
                    {alert.actions.map((action, i) => (
                      <li key={i}>→ {action}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AlertsPanel;
